import type { QueryClient } from '@tanstack/react-query';
import { api, type VaultMembershipDto } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

type VaultMembershipsData = Awaited<ReturnType<typeof api.listMyVaultMemberships>>;

/**
 * Synchronously add a just-created workspace's owner membership to the
 * memberships cache so the workspace switcher reflects it IMMEDIATELY, instead
 * of racing a refetch.
 *
 * This is the fix for the long-standing "can't switch to a new workspace until
 * I log out and back in" bug. The prior approach (`await invalidateQueries`
 * before switching) still lost the selection: with `staleTime` and
 * `refetchOnWindowFocus: 'always'`, a refetch that *started before* the create
 * committed could dedupe in and repopulate the cache with a pre-create
 * membership list — and `useHolderSwitcher`'s auto-clear effect then dropped
 * the freshly-selected workspace because it wasn't in that stale list.
 *
 * Server-side the new membership is created atomically and the
 * `/vault/me/memberships` query returns it immediately (verified via prod
 * logs: no errors, the new holder is valid at once). So the only problem was
 * client cache timing. We:
 *   1. `cancelQueries` — abort any in-flight (possibly pre-create) refetch so
 *      it can't clobber what we're about to write.
 *   2. `setQueryData` — insert the server-returned owner membership directly.
 *
 * The caller should fire a background `invalidateQueries` afterwards to
 * reconcile with the server (which also returns this membership, so the
 * optimistic entry is confirmed rather than lost).
 */
export async function primeCreatedWorkspaceInCache(
  qc: QueryClient,
  ownerMembership: VaultMembershipDto,
): Promise<void> {
  await qc.cancelQueries({ queryKey: queryKeys.vault.me.memberships() });
  qc.setQueryData<VaultMembershipsData>(queryKeys.vault.me.memberships(), (old) =>
    old && !old.memberships.some((m) => m.holderId === ownerMembership.holderId)
      ? { ...old, memberships: [...old.memberships, ownerMembership] }
      : old,
  );
}
